"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PriceIntroSchema = void 0;
exports.lineAmountAtMonth = lineAmountAtMonth;
exports.buildPricePhases = buildPricePhases;
exports.phaseHeadline = phaseHeadline;
exports.phaseDisclosure = phaseDisclosure;
exports.phasePeak = phasePeak;
exports.expectedAtMonth = expectedAtMonth;
exports.phaseDrift = phaseDrift;
exports.remainingPhasesFrom = remainingPhasesFrom;
const zod_1 = require("zod");
/**
 * PRICE PHASES — one subscription whose price changes over time.
 *
 * The platform sells three things that all change price on a schedule, and
 * until now only one of them was modelled:
 *
 *  - a FINANCED DEVICE stops billing at the end of its term (already handled,
 *    by `scheduleFixedTermItems` — the boundary case this generalises);
 *  - a SOFTWARE PACKAGE offered at an introductory rate ("$5 for 12 months,
 *    then $99");
 *  - a DEVICE offered the same way.
 *
 * All three are the same shape: a line whose amount is X for some months and Y
 * afterwards. This module turns a set of such lines into the ordered PHASES a
 * Stripe subscription schedule needs, and it is the ONLY place that arithmetic
 * lives — the browser previews from it, the server bills from it, and the
 * monthly verifier checks against it.
 *
 * ---------------------------------------------------------------------------
 * WHY A PURE MODEL, AND WHY IT IS THE WHOLE POINT
 * ---------------------------------------------------------------------------
 *
 * A schedule phase REPLACES a subscription's items rather than amending them.
 * Get one wrong and there is no error to catch: a customer is billed the
 * introductory price forever, or dropped to nothing, and it surfaces on a bank
 * statement months later. That risk cannot be tested away at the Stripe seam,
 * so the decision of "what should be billed, when" is made here — in a pure,
 * total function with no I/O — and the Stripe layer is left with nothing to do
 * but transcribe it.
 *
 * Every amount is integer cents. Every duration is whole BILLING CYCLES, which
 * on a monthly subscription is months — the same unit `termMonths` already
 * means, and the same unit Stripe's `iterations` counts. Nothing here converts
 * between the two, because that conversion is exactly how a 24-month term
 * becomes a 24-YEAR one (D8).
 */
// ---------------------------------------------------------------------------
// The offer, as an operator sets it
// ---------------------------------------------------------------------------
/**
 * An INTRODUCTORY price: this amount for this many months, then the standard
 * one.
 *
 * Deliberately expressed as "intro + duration" rather than as a list of
 * arbitrary segments. An operator sells "$5 for 12 months, then $99"; they do
 * not sell a four-segment step function, and a shape that allowed one would be
 * a shape someone could get wrong. Two prices and a boundary is the whole
 * product decision.
 *
 * `introMonths: 0` is not an offer and is refused — it would mean an intro
 * price that never applies, which is a mistake every time it is written.
 */
exports.PriceIntroSchema = zod_1.z.object({
    /** What the customer pays during the introductory period, integer cents. `0` is a real value: free. */
    introPriceCents: zod_1.z.number().int().min(0),
    /** How many billing cycles the introductory price runs for. */
    introMonths: zod_1.z.number().int().min(1),
    /**
     * Why it was given. INTERNAL — same rule as `DiscountSchema.reason`: it holds
     * negotiation notes and must never reach a customer-facing surface.
     */
    reason: zod_1.z.string().optional(),
});
/** What one line costs at a given cycle, or `undefined` once it has ended. */
function lineAmountAtMonth(line, month) {
    if (typeof line.endsAfterMonths === 'number' && month >= line.endsAfterMonths)
        return undefined;
    if (line.intro && month < line.intro.introMonths)
        return Math.max(0, Math.round(line.intro.introPriceCents));
    return Math.max(0, Math.round(line.standardAmountCents));
}
/**
 * THE PHASES a set of lines produces. Pure and total.
 *
 * Boundaries are the union of every point at which ANY line changes — the end
 * of an introductory period, or the end of a device term. Between two
 * consecutive boundaries every line is constant by construction, which is
 * exactly what makes a phase a phase.
 *
 * The last phase is OPEN-ENDED (no `iterations`) whenever anything is still
 * billing after the final boundary. When nothing is — every line was a device
 * that has now been paid off — the schedule simply ends, and the caller decides
 * whether that means cancelling the subscription. This function does not invent
 * a trailing empty phase, because a phase billing nothing is a Stripe error and
 * an invoice for $0 to a customer.
 *
 * A line that costs 0 during a phase is OMITTED from it rather than carried at
 * zero: a $0 recurring line on an invoice tells the customer they are buying
 * something for free, and `ensureNetPrice` refuses to mint a price for it
 * anyway. A phase in which EVERY line is 0 (a fully free introductory period)
 * still exists, with no lines and a total of 0 — the caller renders that as a
 * free period rather than as a charge, and it is the one case where a phase
 * legitimately bills nothing.
 */
function buildPricePhases(lines) {
    const live = (lines || []).filter((l) => l && l.quantity > 0);
    if (live.length === 0)
        return [];
    // Every cycle at which something changes. `0` is the start, never a boundary.
    const boundaries = new Set();
    for (const line of live) {
        if (line.intro && line.intro.introMonths > 0)
            boundaries.add(line.intro.introMonths);
        if (typeof line.endsAfterMonths === 'number' && line.endsAfterMonths > 0) {
            boundaries.add(line.endsAfterMonths);
        }
    }
    const ordered = [...boundaries].sort((a, b) => a - b);
    const phases = [];
    let start = 0;
    const phaseAt = (month, iterations) => {
        const phaseLines = [];
        let total = 0;
        let anythingAlive = false;
        for (const line of live) {
            const amount = lineAmountAtMonth(line, month);
            if (amount === undefined)
                continue; // this line has ended
            anythingAlive = true;
            if (amount === 0)
                continue; // free: alive, but no billable line
            phaseLines.push({ key: line.key, quantity: line.quantity, amountCents: amount });
            total += amount * line.quantity;
        }
        // Nothing is alive at all — every line has ended. There is no phase here.
        if (!anythingAlive)
            return undefined;
        return {
            startMonth: month,
            ...(iterations !== undefined ? { iterations } : {}),
            lines: phaseLines,
            totalCents: total,
        };
    };
    for (const boundary of ordered) {
        const iterations = boundary - start;
        // Two lines whose boundaries coincide produce one boundary, not a
        // zero-length phase between them.
        if (iterations <= 0)
            continue;
        const phase = phaseAt(start, iterations);
        if (phase)
            phases.push(phase);
        start = boundary;
    }
    // Whatever is still billing after the last boundary runs on, open-ended.
    const tail = phaseAt(start);
    if (tail)
        phases.push(tail);
    return phases;
}
function phaseHeadline(phases) {
    if (!phases || phases.length === 0) {
        return { firstCents: 0, settlesAtCents: 0, changes: false };
    }
    const first = phases[0];
    const last = phases[phases.length - 1];
    return {
        firstCents: first.totalCents,
        settlesAtCents: last.totalCents,
        changes: first.totalCents !== last.totalCents,
        ...(phases.length > 1 ? { changesAtMonth: phases[1].startMonth } : {}),
    };
}
/**
 * The customer-facing sentence a phased price MUST carry.
 *
 * An introductory price a customer is not told expires is the most
 * complained-about pattern in subscription billing, and disclosing it is not
 * optional in several jurisdictions. So the disclosure is generated from the
 * SAME phases that bill, rather than typed into a marketing field where it
 * could say one thing while the schedule does another.
 *
 * ---------------------------------------------------------------------------
 * IT DESCRIBES EVERY PHASE, NOT JUST THE FIRST AND LAST
 * ---------------------------------------------------------------------------
 *
 * This function originally read the first phase and the last one and joined
 * them with "then". On a real schedule that is a LIE BY OMISSION, and the
 * live example is the ordinary one: a package at $5 for 12 months beside a
 * terminal financed over 24 gives
 *
 *      months 0-11   $152      (intro package + hardware)
 *      months 12-23  $246      (full package + hardware)   <- omitted
 *      month  24+    $128      (hardware paid off)
 *
 * and first-vs-last produced *"$152.00/month for 12 months, then
 * $128.00/month"* — which hides the year at $246, the single highest price the
 * customer ever pays and the one they are least expecting. A customer told that
 * sentence has been misled about the most expensive part of their contract.
 *
 * So every phase is named, in order. A schedule with more steps produces a
 * longer sentence; that is correct, and a sentence that is too long to show is
 * a signal that the OFFER is too complicated to sell, not that the disclosure
 * should be trimmed.
 *
 * Returns `null` only when the price genuinely never changes.
 */
function phaseDisclosure(phases, format) {
    if (!phases || phases.length < 2)
        return null;
    const period = (months) => (months === 1 ? '1 month' : `${months} months`);
    const amount = (cents) => (cents === 0 ? 'Free' : `${format(cents)}/month`);
    const parts = [];
    phases.forEach((phase, i) => {
        const last = i === phases.length - 1;
        if (last || phase.iterations === undefined) {
            // The open-ended tail. "then $X/month" — no duration, because there
            // isn't one.
            parts.push(`then ${amount(phase.totalCents)}`);
            return;
        }
        const lead = i === 0 ? '' : 'then ';
        parts.push(`${lead}${amount(phase.totalCents)} for ${period(phase.iterations)}`);
    });
    // "Free for 3 months" reads better than "Free/month for 3 months".
    const sentence = parts.join(', ').replace(/Free\/month/g, 'Free');
    return `${sentence.charAt(0).toUpperCase()}${sentence.slice(1)}.`;
}
/**
 * The highest amount the customer will ever be charged, and when.
 *
 * Exists because `phaseHeadline`'s `settlesAtCents` is NOT the worst case: a
 * schedule can peak in the middle (full software price while hardware is still
 * financing) and fall afterwards. Any surface that asks a customer to consent
 * to a schedule should show them this figure, not just the one they start on.
 */
function phasePeak(phases) {
    if (!phases || phases.length === 0)
        return null;
    let peak = phases[0];
    for (const phase of phases)
        if (phase.totalCents > peak.totalCents)
            peak = phase;
    return { cents: peak.totalCents, month: peak.startMonth };
}
// ---------------------------------------------------------------------------
// Verification — what the monthly reconciliation compares
// ---------------------------------------------------------------------------
/**
 * What SHOULD be billing at a given cycle, from the schedule that was agreed.
 *
 * This is the half of reconciliation that does not talk to Stripe: given the
 * phases recorded when the subscription was created and how many cycles have
 * elapsed, it says what the invoice ought to come to. A verifier compares it
 * with what Stripe actually charged and alerts on any difference.
 *
 * Returns `undefined` past the end of a schedule that has none — every line
 * ended — which is a subscription that should be billing nothing at all.
 */
function expectedAtMonth(phases, month) {
    if (!phases || phases.length === 0 || month < 0)
        return undefined;
    for (const phase of phases) {
        if (phase.iterations === undefined) {
            // The open-ended tail: everything from its start onwards.
            if (month >= phase.startMonth)
                return phase;
            continue;
        }
        if (month >= phase.startMonth && month < phase.startMonth + phase.iterations)
            return phase;
    }
    return undefined;
}
/**
 * Compare what Stripe charged against what the agreed schedule says.
 *
 * Returns `null` when they match — the overwhelmingly common case, and the one
 * that must be cheap and silent. Any difference at all is returned, however
 * small: a one-cent drift is a rounding bug worth knowing about, and a
 * tolerance would be the thing that hid a real one.
 */
function phaseDrift(phases, month, actualCents) {
    const expected = expectedAtMonth(phases, month);
    const expectedCents = expected ? expected.totalCents : 0;
    if (expectedCents === actualCents)
        return null;
    return {
        month,
        expectedCents,
        actualCents,
        differenceCents: actualCents - expectedCents,
    };
}
/**
 * THE PHASES STILL TO RUN, as of `month`. Pure and total.
 *
 * Rebuilding a live schedule means resending the phases that have not finished.
 * This takes a ladder built from month 0 — `buildPricePhases` output — and
 * returns the part of it that is still ahead, renumbered so the first phase
 * starts at 0, because Stripe is told durations rather than absolute months.
 *
 * A phase whose final cycle has already completed is DROPPED, not emitted with
 * `iterations: 0`: a phase running for no cycles is not a phase and Stripe
 * rejects it, and emitting one would bill the elapsed step a second time.
 *
 * An empty result means the ladder has fully elapsed and nothing is left to
 * bill — the caller decides whether that means releasing the schedule or
 * cancelling the subscription. This function does not decide that.
 */
function remainingPhasesFrom(phases, month) {
    const from = Number.isFinite(month) && month > 0 ? Math.floor(month) : 0;
    const out = [];
    let startMonth = 0;
    for (const phase of phases || []) {
        // The open-ended final phase has no end; it is always still ahead.
        const ends = typeof phase.iterations === 'number' ? phase.startMonth + phase.iterations : undefined;
        if (typeof ends === 'number' && ends <= from)
            continue; // fully elapsed
        const consumed = Math.max(0, from - phase.startMonth);
        const remaining = typeof phase.iterations === 'number' ? phase.iterations - consumed : undefined;
        if (typeof remaining === 'number' && remaining <= 0)
            continue;
        out.push({
            startMonth,
            ...(typeof remaining === 'number' ? { iterations: remaining } : {}),
            lines: phase.lines.map((l) => ({ ...l })),
            totalCents: phase.totalCents,
        });
        if (typeof remaining === 'number')
            startMonth += remaining;
    }
    return out;
}
//# sourceMappingURL=pricePhases.js.map